import { Component, lazy, Suspense } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton, Typography, useTheme } from '@mui/material'
import type {
  EmojiClickData,
  EmojiStyle,
  Theme as EmojiPickerTheme,
} from 'emoji-picker-react'

// Библиотека тяжёлая (~МБ данных emoji) — только через React.lazy,
// чтобы Vite вынес её в отдельный чанк и не раздувал основной бандл.
const EmojiPicker = lazy(() => import('emoji-picker-react'))

interface LazyEmojiPickerProps {
  onPick: (emoji: string) => void
  width?: number
  height?: number
}

interface BoundaryProps {
  fallback: ReactNode
  children: ReactNode
}

/**
 * Первый React.lazy в приложении: упавший dynamic import (устаревший хеш
 * чанка после деплоя, сеть) без boundary валил бы страницу целиком.
 */
class PickerErrorBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}

/**
 * Единственная обёртка emoji-picker-react (чат и EmojiField админки):
 * один lazy-чанк, одно место для enum-кастов и конфигурации пикера.
 */
export function LazyEmojiPicker({ onPick, width = 350, height = 420 }: LazyEmojiPickerProps) {
  const { t } = useTranslation()
  const theme = useTheme()

  return (
    <PickerErrorBoundary
      fallback={
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, width, boxSizing: 'border-box' }}>
          {t('chat.emojiUnavailable')}
        </Typography>
      }
    >
      <Suspense fallback={<Skeleton variant="rounded" width={width} height={height} />}>
        <EmojiPicker
          onEmojiClick={(data: EmojiClickData) => onPick(data.emoji)}
          searchPlaceHolder={t('chat.emojiSearch')}
          // Enum-типы библиотеки (Theme/EmojiStyle) — строковые значения;
          // статически импортировать их нельзя (сломает lazy-чанк), поэтому
          // касты — здесь и только здесь.
          theme={theme.palette.mode as unknown as EmojiPickerTheme}
          // native: пикер показывает системные глифы ОС — те же, что в поле
          // ввода и в сообщениях (дефолт apple рисовал картинки с CDN).
          emojiStyle={'native' as unknown as EmojiStyle}
          // Cap каталога: шрифт Windows 10 не знает emoji новее Unicode 12 —
          // без ограничения новые символы видны «тофу» (□).
          emojiVersion="12.0"
          width={width}
          height={height}
          lazyLoadEmojis
          skinTonesDisabled={false}
          previewConfig={{ showPreview: false }}
        />
      </Suspense>
    </PickerErrorBoundary>
  )
}
